import { TextInput } from "react-admin";
import { FormProvider, useForm } from "react-hook-form";
import { DateTime } from "luxon";
import { DateTimeInput } from "../../inputs/DateTimeInput";
import { Column } from "../Column";
import { Row } from "../Row";
import { SelectInput } from "../../inputs/SelectInput";
import { TimeInput } from "../../inputs/TimeInput";
import { Form2Theme } from "../Form2";
import { ReactElement, useMemo } from "react";
import { SaveButton2 } from "../../buttons/SaveButton2";
import { VehicleInput } from "../../inputs/VehicleInput";
import { VehicleEvent } from "../../model/autovioCalendarEvents";
import { l10n } from "../../l10n";

export interface VehicleEventFormValues {
  start: null | DateTime;
  end: null | DateTime;
  noteForInstructor: null | string;
  vehicleId: null | string;
  vehicleEventType: null | string;
}

export function VehicleEventForm(props: {
  initialDateTime?: DateTime;
  vehicleId?: string;
  vehicleEvent?: VehicleEvent;
  onSubmit: (formValues: VehicleEventFormValues) => Promise<void>;
  backButton?: ReactElement;
}) {
  const { initialDateTime, vehicleId, vehicleEvent, onSubmit, backButton } = props;
  const defaultValues = useMemo<VehicleEventFormValues>(() => {
    return vehicleEvent
      ? {
          start: vehicleEvent.start,
          end: vehicleEvent.end,
          vehicleId: vehicleEvent.vehicleId,
          vehicleEventType: vehicleEvent.vehicleEventType,
          noteForInstructor: vehicleEvent.noteForInstructor ?? "",
        }
      : {
          start: initialDateTime ?? null,
          end: null,
          vehicleId: vehicleId ?? null,
          vehicleEventType: null,
          noteForInstructor: null,
        };
  }, [vehicleEvent, initialDateTime, vehicleId]);
  const formProps = useForm<VehicleEventFormValues>({ defaultValues });
  const [start] = formProps.watch(["start"]);

  return (
    <Form2Theme>
      <FormProvider {...formProps}>
        <form onSubmit={formProps.handleSubmit(onSubmit)} style={{ height: "100%" }}>
          <Column height="100%" minHeight={500} gap="1em">
            {!vehicleId && <VehicleInput source="vehicleId" label="Fahrzeug" size="small" />}
            <SelectInput
              label="Art"
              source="vehicleEventType"
              options={Object.entries(l10n.vehicleEventType).map(([type, label]) => [type, label])}
              validate={(value) => (value ? true : "Bitte wähle die Art des Termins aus.")}
              size="small"
              required
            />
            <DateTimeInput label="Start" source="start" allowPastDates size="small" />
            <TimeInput
              source="end"
              label="Ende"
              sx={{ width: "100%" }}
              size="small"
              validate={(end) => {
                if (start?.isValid && end?.isValid) {
                  if (end.hour < start.hour || (end.hour === start.hour && end.minute <= start.minute)) {
                    return "Der Endzeitpunkt muss nach dem Startzeitpunkt liegen.";
                  }
                }
                return true;
              }}
            />
            <TextInput label="Notizen" source="noteForInstructor" rows={4} fullWidth multiline size="small" />
            <Row sx={{ justifyContent: "space-between", mt: "auto" }}>
              {backButton || <div />}
              <SaveButton2 />
            </Row>
          </Column>
        </form>
      </FormProvider>
    </Form2Theme>
  );
}
